import React from 'react';
import { Dialog } from '../reusable';
import { Building2, Globe, Linkedin, Mail, MapPin, Phone, Hash, User, Layers } from 'lucide-react';

const formatMoney = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const Field = ({ label, value, isDarkMode }) => {
  const muted = isDarkMode ? '#a1a1aa' : '#64748b';
  const text = isDarkMode ? '#f4f4f5' : '#0f172a';
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ fontSize: '11px', fontWeight: 600, color: muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: '14px', color: text, marginTop: '2px', wordBreak: 'break-word' }}>{value || '—'}</div>
    </div>
  );
};

/**
 * Propuesta inicial de ventana de detalle (refinable luego).
 * Layout: cabecera con identidad, cuerpo en rejilla, contactos y métricas.
 */
const OrganizationDetailModal = ({
  isOpen,
  onClose,
  organization,
  contacts = [],
  units = [],
  unitsLoading = false,
  quotations = [],
  sales = [],
  commercialLoading = false,
  quotedTotal = 0,
  isDarkMode = false,
  onEdit,
}) => {
  if (!organization) return null;

  const initial = String(organization.org_name || '?').trim().charAt(0).toUpperCase() || '?';

  const body = (
    <div style={{ padding: '0 20px 20px', overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>
      <div
        style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'flex-start',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}`,
        }}
      >
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '12px',
            background: isDarkMode ? '#27272a' : '#f1f5f9',
            display: 'grid',
            placeItems: 'center',
            fontSize: '22px',
            fontWeight: 800,
            color: isDarkMode ? '#fafafa' : '#0f172a',
            flexShrink: 0,
          }}
        >
          {initial}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: '12px', color: isDarkMode ? '#a1a1aa' : '#64748b' }}>Código · {organization.org_code || '—'}</p>
          <p style={{ margin: '4px 0 0', fontSize: '18px', fontWeight: 700, color: isDarkMode ? '#f4f4f5' : '#0f172a' }}>{organization.org_name}</p>
          <p style={{ margin: '6px 0 0', fontSize: '13px', color: isDarkMode ? '#d4d4d8' : '#475569' }}>
            Segmento <strong>{organization.segment || '—'}</strong>
            {organization.status ? (
              <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '999px', fontSize: '11px', background: '#dcfce7', color: '#166534' }}>
                {organization.status}
              </span>
            ) : null}
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px 24px' }}>
        <div>
          <h4 style={{ margin: '0 0 10px', fontSize: '13px', color: isDarkMode ? '#e4e4e7' : '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Hash size={16} /> Identificación
          </h4>
          <Field label="RUT" value={organization.rut} isDarkMode={isDarkMode} />
          <Field label="Responsable" value={organization.assignee_name} isDarkMode={isDarkMode} />
          <Field label="Creado por" value={organization.created_by} isDarkMode={isDarkMode} />
        </div>
        <div>
          <h4 style={{ margin: '0 0 10px', fontSize: '13px', color: isDarkMode ? '#e4e4e7' : '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <MapPin size={16} /> Ubicación
          </h4>
          <Field label="Dirección" value={organization.address} isDarkMode={isDarkMode} />
          <Field label="Comuna" value={organization.commune} isDarkMode={isDarkMode} />
          <Field label="Región" value={organization.region} isDarkMode={isDarkMode} />
          <Field label="País" value={organization.country} isDarkMode={isDarkMode} />
        </div>
        <div>
          <h4 style={{ margin: '0 0 10px', fontSize: '13px', color: isDarkMode ? '#e4e4e7' : '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Building2 size={16} /> Segmento comercial
          </h4>
          <Field label="Canal" value={organization.channel} isDarkMode={isDarkMode} />
          <Field label="Industria" value={organization.industry} isDarkMode={isDarkMode} />
          <Field label="Subindustria" value={organization.subindustry} isDarkMode={isDarkMode} />
        </div>
        <div>
          <h4 style={{ margin: '0 0 10px', fontSize: '13px', color: isDarkMode ? '#e4e4e7' : '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
            Contacto corporativo
          </h4>
          <Field label="Email" value={organization.email} isDarkMode={isDarkMode} />
          <Field label="Teléfono" value={organization.phone} isDarkMode={isDarkMode} />
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px', flexWrap: 'wrap' }}>
            {organization.website ? (
              <a href={organization.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Globe size={14} /> Web
              </a>
            ) : null}
            {organization.linkedin ? (
              <a href={organization.linkedin} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Linkedin size={14} /> LinkedIn
              </a>
            ) : null}
          </div>
        </div>
      </div>

      <div style={{ marginTop: '20px', padding: '14px', borderRadius: '10px', background: isDarkMode ? '#18181b' : '#f8fafc', border: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}` }}>
        <h4 style={{ margin: '0 0 12px', fontSize: '13px', color: isDarkMode ? '#e4e4e7' : '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Layers size={16} /> Subunidades ({unitsLoading ? '…' : units.length})
        </h4>
        {unitsLoading ? (
          <p style={{ margin: 0, fontSize: '13px', color: isDarkMode ? '#a1a1aa' : '#64748b' }}>Cargando subunidades…</p>
        ) : units.length === 0 ? (
          <p style={{ margin: 0, fontSize: '13px', color: isDarkMode ? '#a1a1aa' : '#64748b' }}>Sin subunidades registradas.</p>
        ) : (
          <div style={{ display: 'grid', gap: '10px' }}>
            {units.map((u) => (
              <div
                key={u.id}
                style={{
                  padding: '10px',
                  borderRadius: '8px',
                  background: isDarkMode ? '#27272a' : '#fff',
                  border: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}`,
                }}
              >
                <div style={{ fontWeight: 600, fontSize: '14px', color: isDarkMode ? '#fafafa' : '#0f172a' }}>{u.unit_name || '—'}</div>
                {u.unit_code ? (
                  <div style={{ fontSize: '11px', color: isDarkMode ? '#a1a1aa' : '#64748b', marginTop: '2px' }}>Código: {u.unit_code}</div>
                ) : null}
                <div style={{ fontSize: '12px', color: isDarkMode ? '#d4d4d8' : '#475569', marginTop: '6px' }}>
                  {[u.address, u.commune, u.region, u.country].filter(Boolean).join(' · ') || '—'}
                </div>
                {u.notes ? (
                  <div style={{ fontSize: '12px', color: isDarkMode ? '#a1a1aa' : '#64748b', marginTop: '6px' }}>{u.notes}</div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginTop: '20px', padding: '14px', borderRadius: '10px', background: isDarkMode ? '#18181b' : '#f8fafc', border: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}` }}>
        <h4 style={{ margin: '0 0 12px', fontSize: '13px', color: isDarkMode ? '#e4e4e7' : '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <User size={16} /> Contactos vinculados ({contacts.length})
        </h4>
        <div style={{ display: 'grid', gap: '10px' }}>
          {contacts.length === 0 ? (
            <p style={{ margin: 0, fontSize: '13px', color: isDarkMode ? '#a1a1aa' : '#64748b' }}>Sin contactos registrados.</p>
          ) : (
            contacts.map((c) => (
              <div
                key={c.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: '8px',
                  alignItems: 'start',
                  padding: '10px',
                  borderRadius: '8px',
                  background: isDarkMode ? '#27272a' : '#fff',
                  border: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}`,
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: isDarkMode ? '#fafafa' : '#0f172a' }}>{c.full_name}</div>
                  <div style={{ fontSize: '12px', color: isDarkMode ? '#a1a1aa' : '#64748b' }}>{c.role || '—'}</div>
                  <div style={{ fontSize: '12px', marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                    {c.email ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: isDarkMode ? '#d4d4d8' : '#475569' }}>
                        <Mail size={12} /> {c.email}
                      </span>
                    ) : null}
                    {c.phone ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: isDarkMode ? '#d4d4d8' : '#475569' }}>
                        <Phone size={12} /> {c.phone}
                      </span>
                    ) : null}
                  </div>
                </div>
                <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', background: '#e0f2fe', color: '#0369a1' }}>{c.status || '—'}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <div style={{ marginTop: '20px', padding: '14px', borderRadius: '10px', background: isDarkMode ? '#18181b' : '#f8fafc', border: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}` }}>
        <h4 style={{ margin: '0 0 12px', fontSize: '13px', color: isDarkMode ? '#e4e4e7' : '#334155' }}>
          Cotizaciones asociadas ({commercialLoading ? '…' : quotations.length})
        </h4>
        {commercialLoading ? (
          <p style={{ margin: 0, fontSize: '13px', color: isDarkMode ? '#a1a1aa' : '#64748b' }}>Cargando cotizaciones…</p>
        ) : quotations.length === 0 ? (
          <p style={{ margin: 0, fontSize: '13px', color: isDarkMode ? '#a1a1aa' : '#64748b' }}>Sin cotizaciones asociadas.</p>
        ) : (
          <div style={{ display: 'grid', gap: '8px' }}>
            {quotations.map((q) => (
              <div
                key={q.id}
                style={{
                  padding: '10px',
                  borderRadius: '8px',
                  background: isDarkMode ? '#27272a' : '#fff',
                  border: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '10px',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ fontSize: '13px', color: isDarkMode ? '#fafafa' : '#0f172a' }}>
                  <strong>{q.quotation_code || `Q-${q.id}`}</strong> · {q.status || 'Sin estado'}
                </div>
                <div style={{ fontSize: '13px', color: isDarkMode ? '#d4d4d8' : '#334155' }}>
                  {formatMoney(q.total_amount || 0)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginTop: '20px', padding: '14px', borderRadius: '10px', background: isDarkMode ? '#18181b' : '#f8fafc', border: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}` }}>
        <h4 style={{ margin: '0 0 12px', fontSize: '13px', color: isDarkMode ? '#e4e4e7' : '#334155' }}>
          Ventas asociadas ({commercialLoading ? '…' : sales.length})
        </h4>
        {commercialLoading ? (
          <p style={{ margin: 0, fontSize: '13px', color: isDarkMode ? '#a1a1aa' : '#64748b' }}>Cargando ventas…</p>
        ) : sales.length === 0 ? (
          <p style={{ margin: 0, fontSize: '13px', color: isDarkMode ? '#a1a1aa' : '#64748b' }}>Sin ventas asociadas.</p>
        ) : (
          <div style={{ display: 'grid', gap: '8px' }}>
            {sales.map((s) => (
              <div
                key={s.id}
                style={{
                  padding: '10px',
                  borderRadius: '8px',
                  background: isDarkMode ? '#27272a' : '#fff',
                  border: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '10px',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ fontSize: '13px', color: isDarkMode ? '#fafafa' : '#0f172a' }}>
                  <strong>{s.sale_code || `V-${s.id}`}</strong> · {s.quotation_code || 'Sin cotización'}
                </div>
                <div style={{ fontSize: '13px', color: isDarkMode ? '#d4d4d8' : '#334155' }}>
                  {formatMoney(s.total_amount || 0)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
        <div style={{ padding: '12px', borderRadius: '10px', border: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}` }}>
          <div style={{ fontSize: '11px', color: isDarkMode ? '#a1a1aa' : '#64748b', textTransform: 'uppercase' }}>Monto vendido (registrado)</div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#ea580c', marginTop: '4px' }}>{formatMoney(organization.sales_amount)}</div>
        </div>
        <div style={{ padding: '12px', borderRadius: '10px', border: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}` }}>
          <div style={{ fontSize: '11px', color: isDarkMode ? '#a1a1aa' : '#64748b', textTransform: 'uppercase' }}>Total cotizado (suma cotizaciones)</div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#2563eb', marginTop: '4px' }}>{formatMoney(quotedTotal)}</div>
        </div>
        <div style={{ padding: '12px', borderRadius: '10px', border: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}` }}>
          <div style={{ fontSize: '11px', color: isDarkMode ? '#a1a1aa' : '#64748b', textTransform: 'uppercase' }}>Conteos</div>
          <div style={{ fontSize: '13px', marginTop: '6px', color: isDarkMode ? '#e4e4e7' : '#334155' }}>
            Ventas: {organization.sales_count ?? 0} · Cotizaciones: {organization.quotations_count ?? 0} · Contactos: {organization.contacts_count ?? 0}
          </div>
        </div>
      </div>

      {onEdit ? (
        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button
            type="button"
            onClick={onEdit}
            style={{
              height: '40px',
              padding: '0 18px',
              borderRadius: '8px',
              border: 'none',
              background: '#2563eb',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Editar organización
          </button>
        </div>
      ) : null}
    </div>
  );

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Ficha de organización" subtitle="Vista consolidada — diseño inicial" isDarkMode={isDarkMode} size="lg">
      {body}
    </Dialog>
  );
};

export default OrganizationDetailModal;
